// HistorialComportamientoReportesOperadores.jsx
import React, { useEffect, useMemo, useState, useContext } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  CircularProgress,
  Button,
  Tooltip,
  styled 
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import GeneralContext from "../GeneralContext/GeneralContext";

import PageBody from "../GeneralComponents/PageBody";
import InputFilter from "../GeneralComponents/InputFilter";

import axios from "../../api/axios";
import { ROL_OP_ADMIN, ROL_OP_INS } from "../../Constants/Constants";


const BlueTooltip = styled(({ className, ...props }) => (
  <Tooltip arrow placement="top" {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: "#003B8DE5", 
    padding: theme.spacing(0.5, 1),
    fontWeight: 500,
  },
  [`& .MuiTooltip-arrow`]: {
    color: "#003B8DE5",
  },
}));

// =====================
// UI constants/helpers
// =====================
const MONTHS = [
  { key: "01", label: "Enero", short: "enero" },
  { key: "02", label: "Febrero", short: "febrero" },
  { key: "03", label: "Marzo", short: "marzo" },
  { key: "04", label: "Abril", short: "abril" },
  { key: "05", label: "Mayo", short: "mayo" },
  { key: "06", label: "Junio", short: "junio" },
  { key: "07", label: "Julio", short: "julio" },
  { key: "08", label: "Agosto", short: "agosto" },
  { key: "09", label: "Septiembre", short: "septiembre" },
  { key: "10", label: "Octubre", short: "octubre" },
  { key: "11", label: "Noviembre", short: "noviembre" },
  { key: "12", label: "Diciembre", short: "diciembre" },
];

const FUENTES_VACIO_PERMITIDO = new Set([
  "vw_acuerdosprivados",
  "vw_altas_bajas",
  "vw_maliciosas",
  "vw_cambio_configuracion",
]);

const TZ_CLT = "America/Santiago";

const USO_FUENTES = new Set([
  "vw_visitas_zona",
  "vw_visitas_top10",
  "vw_visitas_interes_mineduc",
  "vw_control_parental",
  "vw_seguridad",
  "vw_visitas_por_categoria",
  "vw_dominios_visitados",
  "vw_dominios_interes_visitados",
]);

function isUsoFuente(fuente) {
  return USO_FUENTES.has(normFuente(fuente));
}

// Devuelve YYYYMMDD (número) de un Date pero "visto" en un timezone (CLT)
function ymdKeyInTimeZone(dateObj, timeZone) {
  if (!dateObj) return null;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(dateObj);

  const get = (t) => parts.find((p) => p.type === t)?.value;
  const y = Number(get("year"));
  const m = Number(get("month"));
  const d = Number(get("day"));
  return y * 10000 + m * 100 + d;
}

// Fecha límite desde efemérides (sin convertir TZ)
function deadlineDateFromDetail({ fuente, ym, detail }) {
  const pick10 = isUsoFuente(fuente);

  // ojo: estos vienen por periodo (p.efemerides), tú los guardaste en detail
  const raw = pick10 ? detail?.dia_habil_10 : detail?.dia_habil_5;

  const dl = parseDateLoose(raw);
  if (dl) return dl;

  // fallback por si no viene en API (mantiene tu lógica anterior)
  return deadlineForPeriodYM(ym, fuente);
}

// true si recibido_en (UTC→CLT) está después del deadline (solo por fecha)
function isFueraPlazo({ fuente, ym, detail }) {
  const recUtc = parseDateLoose(detail?.fecha_recepcion ?? detail?.recibido_en);
  const dl = deadlineDateFromDetail({ fuente, ym, detail });

  if (!recUtc || !dl) return false;

  // recibido_en: comparar por FECHA en CLT
  const recKey = ymdKeyInTimeZone(recUtc, TZ_CLT);

  // deadline: comparar por FECHA directa (sin TZ)
  const dlKey = dl.getFullYear() * 10000 + (dl.getMonth() + 1) * 100 + dl.getDate();

  return recKey > dlKey;
}

const VW_LABELS = {
  vw_prefactura: "Prefactura",
  vw_altas_bajas: "Altas/Bajas",
  vw_maliciosas: "Páginas maliciosas",
  vw_cambio_configuracion: "Cambios de configuración",
  vw_mesa_ayuda: "Mesa de ayuda",
  vw_tickets_mesa_ayuda: "Tickets mesa de ayuda",
  vw_mantenimiento: "Labores de mantenimiento",
  vw_visitas_zona: "Visitas mensuales por zona",
  vw_visitas_top10: "Top 10 dominios por zona",
  vw_visitas_interes_mineduc: "Dominios interés ministerial",
  vw_control_parental: "Control parental",
  vw_seguridad: "Eventos de seguridad",
  vw_visitas_por_categoria: "Visitas por categoría",
  vw_dominios_visitados: "Dominios visitados",
  vw_dominios_interes_visitados: "Dominios interés",
  vw_acuerdosprivados: "Acuerdos privados",
};

function prettifyVwName(vwName) {
  return String(vwName)
    .replace(/^vw_/, "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function labelFromApi(api) {
  const key = String(api ?? "").trim().toLowerCase();
  if (!key.startsWith("vw_")) return key;
  return VW_LABELS[key] ?? prettifyVwName(key);
}

const normFuente = (fuente) => String(fuente ?? "").trim().toLowerCase();
const hasVacioPermitidoByFuente = (fuente) => FUENTES_VACIO_PERMITIDO.has(normFuente(fuente));

// Anchos FIJOS
const OP_COL_W = 320;
const MONTH_COL_W = 140;
const DETAIL_PAD_W = 34;

const monthKey = (d) => String(d).slice(0, 7); // "YYYY-MM"
const yearOf = (d) => Number(String(d).slice(0, 4));
const monthsOfYear = (year) => MONTHS.map((m) => `${year}-${m.key}`);

const CURRENT_YEAR = new Date().getFullYear();
function buildYearRange(from = 2025, to = CURRENT_YEAR) {
  const out = [];
  for (let y = from; y <= to; y++) out.push(y);
  return out;
}

const yearBtnSx = (active) => ({
  width: "100%",
  height: 44,
  borderRadius: 0.75,
  textTransform: "none",
  fontWeight: 700,
  justifyContent: "center",
  border: "1px solid rgba(0,0,0,0.15)",
  ...(active
    ? {
        bgcolor: "#AFC7DF",
        color: "#1f2733",
        "&:hover": { bgcolor: "#A3BED8" },
      }
    : {
        bgcolor: "#fff",
        color: "#1f2733",
        "&:hover": { bgcolor: "rgba(0,0,0,0.03)" },
      }),
});

function dotColor(present, total) {
  if (!total) return "#bdbdbd";
  if (present === 0) return "#e53935";
  if (present < total) return "#e53935";
  return "#43a047";
}

// =====================
// Plazos (días hábiles)
// =====================
function isBusinessDay(date) {
  const d = date.getDay(); // 0 dom, 6 sab
  return d !== 0 && d !== 6;
}

/**
 * N-ésimo día hábil (Lun-Vie) del mes.
 * month0: 0-11
 */
function nthBusinessDay(year, month0, n) {
  let count = 0;
  for (let day = 1; day <= 31; day++) {
    const dt = new Date(year, month0, day);
    if (dt.getMonth() !== month0) break;
    if (isBusinessDay(dt)) {
      count += 1;
      if (count === n) return dt;
    }
  }
  return new Date(year, month0 + 1, 0);
}

function parseDateLoose(x) {
  if (!x) return null;
  const s = String(x).trim();

  const m1 = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m1) return new Date(Number(m1[1]), Number(m1[2]) - 1, Number(m1[3]));

  const m2 = s.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (m2) return new Date(Number(m2[3]), Number(m2[2]) - 1, Number(m2[1]));

  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d;

  return null;
}

function formatDayMonthES(dateObj) {
  if (!dateObj) return "-";
  const day = String(dateObj.getDate()).padStart(2, "0");
  const mon = MONTHS[dateObj.getMonth()]?.short ?? "";
  return `${day} ${mon}`;
}

// Reportes de Uso: 10 primeros días hábiles del mes; resto: 5
function isUsoReportName(name) {
  return String(name ?? "").toLowerCase().includes("uso");
}

function addMonthsYM(ym, addMonths) {
  const y = Number(String(ym).slice(0, 4));
  const m0 = Number(String(ym).slice(5, 7)) - 1;
  const d = new Date(y, m0, 1);
  d.setMonth(d.getMonth() + addMonths);
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${yy}-${mm}`;
}


function deadlineForPeriodYM(ym, reportName) {
  const ymDeadline = addMonthsYM(ym, 0); 

  const y = Number(String(ymDeadline).slice(0, 4));
  const m = Number(String(ymDeadline).slice(5, 7)) - 1;
  const n = isUsoReportName(reportName) ? 10 : 5;
  return nthBusinessDay(y, m, n);
}

// =====================
// Data helpers
// =====================

function normalizeApiData(payloadRaw) {
  const payload = payloadRaw?.data ?? payloadRaw;

  if (payload?.fuentes && Array.isArray(payload.fuentes)) {
    const out = { ...payload };
    out._detailIndex = buildDetailIndexFromOld(out);
    return out;
  }

  const periods = Array.isArray(payload)
    ? payload
    : payload?.periods ?? payload?.periodos ?? payload?.data ?? [];

  const fuenteMap = new Map();
  const detailIndex = new Map(); 

  const ensure = (m, k, factory) => {
    if (!m.has(k)) m.set(k, factory());
    return m.get(k);
  };

  for (const p of periods) {
    const periodoFull = p?.periodo ?? p?.period ?? p?.fecha ?? null;
    if (!periodoFull) continue;
    const ym = monthKey(periodoFull);

    const fuentesArr = p?.fuentes ?? [];
    if (!Array.isArray(fuentesArr)) continue;

    for (const f of fuentesArr) {
      const fuente = f?.fuente ?? f?.nombre_fuente ?? f?.name ?? null;
      if (!fuente) continue;

      const regs = f?.registros ?? f?.operadores ?? f?.data ?? [];
      if (!Array.isArray(regs)) continue;

      const opMap = ensure(fuenteMap, fuente, () => new Map());
      const fuDetail = ensure(detailIndex, fuente, () => new Map());

      for (const r of regs) {
        const opName =
          r?.nombre_operador ??
          null;
        if (!opName) continue;

        const estadoRaw = r?.estado ?? null;
        const estado = String(estadoRaw ?? "").trim();

        const llego = Number(r?.llego ?? 0) === 1;
        const batchError = Number(r?.batch_error ?? 0) === 1;
        const tieneDatos = Number(r?.tiene_datos ?? 0) === 1;

        const fechaRecepRaw = r?.recibido_en ?? r?.fecha_recepcion ?? null;

        let mensajeRaw = r?.batch_errors ?? r?.mensaje ?? null;
        let mensaje = null;

        try {
          if (typeof mensajeRaw === "string" && mensajeRaw.trim().startsWith("[")) {
            const arr = JSON.parse(mensajeRaw);
            if (Array.isArray(arr) && arr.length) {
              const first = arr[0];
              mensaje =
                first?.description ||
                first?.exception ||
                JSON.stringify(first);
            } else {
              mensaje = null;
            }
          } else if (typeof mensajeRaw === "string") {
            mensaje = mensajeRaw;
          } else if (mensajeRaw) {
            mensaje = JSON.stringify(mensajeRaw);
          }
        } catch {
          mensaje = typeof mensajeRaw === "string" ? mensajeRaw : null;
        }

        const isNoLlego = estado === "NO_LLEGO";
        const isOkEnBlanco = estado === "LLEGO_OK_EN_BLANCO";
        const isOkConDatos = estado === "LLEGO_OK_CON_DATOS";
        const isLlegoConError = estado === "LLEGO_CON_ERROR";

        const has = llego && !isNoLlego;

        const vacio = isOkEnBlanco || (has && !tieneDatos && !batchError);

        const vacioPermitido =
          r?.vacio_permitido ??
          false;
        const error = batchError || isLlegoConError;
        const ok = (isOkConDatos && has && !error) || (vacio && !!vacioPermitido && has && !error);
        if (has) ensure(opMap, opName, () => new Set()).add(ym);

        const opDetail = ensure(fuDetail, opName, () => new Map());
        opDetail.set(ym, {
          has,
          fecha_recepcion: fechaRecepRaw,
          estado: estadoRaw,
          mensaje: mensaje,
          vacio,
          vacio_permitido: !!vacioPermitido,
          dia_habil_5: p.efemerides.dia_habil_5,
          dia_habil_6: p.efemerides.dia_habil_6,
          dia_habil_10: p.efemerides.dia_habil_10,
          fuera_plazo: !!(r?.fuera_plazo ?? r?.fueraPlazo ?? r?.late ?? false),
          ok,
          error,
        });
      }
    }
  }

  const years = buildYearRange(2025, CURRENT_YEAR);
  const allMonths = years.flatMap((y) => monthsOfYear(y));

  const fuentesOut = Array.from(fuenteMap.entries())
    .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
    .map(([fuente, opMap]) => {
      const operadores = Array.from(opMap.entries())
        .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
        .map(([nombre_operador, setYM]) => {
          const presentesYM = Array.from(setYM).sort((a, b) => String(a).localeCompare(String(b)));
          const periodos_presentes = presentesYM.map((ym) => `${ym}-01`);
          const faltantesYM = allMonths.filter((ym) => !setYM.has(ym));
          const periodos_faltantes = faltantesYM.map((ym) => `${ym}-01`);
          return { nombre_operador, periodos_presentes, periodos_faltantes };
        });

      return { fuente, total_operadores: operadores.length, operadores };
    });

  const total_operadores = new Set(
    fuentesOut.flatMap((f) => (f.operadores ?? []).map((o) => o.nombre_operador))
  ).size;

  return {
    ok: true,
    total_fuentes: fuentesOut.length,
    total_operadores,
    fuentes: fuentesOut,
    _detailIndex: detailIndex,
  };
}

function buildDetailIndexFromOld(apiData) {
  const idx = new Map();
  const ensure = (m, k, factory) => {
    if (!m.has(k)) m.set(k, factory());
    return m.get(k);
  };

  (apiData?.fuentes ?? []).forEach((f) => {
    const fu = f?.fuente;
    if (!fu) return;
    const fuMap = ensure(idx, fu, () => new Map());

    (f.operadores ?? []).forEach((op) => {
      const name = op?.nombre_operador;
      if (!name) return;
      const opMap = ensure(fuMap, name, () => new Map());

      // en formato antiguo sólo sabemos "presente/faltante"
      (op.periodos_presentes ?? []).forEach((p) => {
        const ym = monthKey(p);
        opMap.set(ym, {
          has: true,
          ok: true,
          error: false,
          vacio: false,
          vacio_permitido: false,
          fecha_recepcion: null,
          mensaje: null,
          estado: "OK",
          fuera_plazo: false,
        });
      });
    });
  });

  return idx;
}

function extractYears(apiData) {
  const years = new Set();
  (apiData?.fuentes ?? []).forEach((f) => {
    (f.operadores ?? []).forEach((op) => {
      (op.periodos_presentes ?? []).forEach((p) => years.add(yearOf(p)));
      (op.periodos_faltantes ?? []).forEach((p) => years.add(yearOf(p)));
    });
  });
  return Array.from(years).filter(Boolean).sort((a, b) => b - a);
}

// =====================
// Conteo "sin error / total" usando _detailIndex
// =====================
function isOkForCount({ fuente, ym, detail }) {
  const estado = String(detail?.estado ?? "").toUpperCase();


    const late = (() => {
    // si API trae fuera_plazo y es true, respétalo (pero si no, calculamos bien)
    if (detail?.fuera_plazo === true) return true;
    return isFueraPlazo({ fuente, ym, detail });
  })();

  // 1) No llegó => NO cuenta
  if (!detail?.has || estado === "NO_LLEGO" || detail?.llego === 0) return false;

  // 2) Error => NO cuenta
  if (detail?.error || estado === "LLEGO_CON_ERROR" || detail?.batch_error === 1) return false;

  // 3) Vacío => cuenta SOLO si:
  //    - está en plazo
  //    - y es permitido para esa fuente
  if (detail?.vacio || estado === "LLEGO_OK_EN_BLANCO") {
    const vacioPermitido =
      detail?.vacio_permitido === true ||
      hasVacioPermitidoByFuente(fuente);

    if (late) return false;      
    if (!vacioPermitido) return false;
    return true;                       
  }

  if (estado === "LLEGO_OK_CON_DATOS" || detail?.ok === true) {
    return !late;
  }

  return false;
}

function buildGrid({ apiData, selectedYear, selectedFuente = null }) {
  const dIndex = apiData?._detailIndex; // Map(fuente -> op -> ym -> detail)

  const months = monthsOfYear(selectedYear);

  const fuentes =
    selectedFuente !== null
      ? [selectedFuente]
      : Array.from(dIndex?.keys?.() ?? []);

  const opsSet = new Set();
  fuentes.forEach((fuente) => {
    const byOp = dIndex?.get?.(fuente);
    if (!byOp) return;
    Array.from(byOp.keys()).forEach((op) => opsSet.add(op));
  });

  const operadores = Array.from(opsSet).sort((a, b) => a.localeCompare(b));
  const total = fuentes.length || 1;

  const rows = operadores.map((opName) => {
    const cells = months.map((ym) => {
      let okCount = 0;

      fuentes.forEach((fuente) => {
        const detail =
          dIndex?.get?.(fuente)?.get?.(opName)?.get?.(ym) ?? { has: false };

        if (isOkForCount({ fuente, ym, detail })) okCount += 1;
      });

      return { ym, present: okCount, total };
    });

    return { operador: opName, cells };
  });

  return { fuentes, months, rows };
}

// =====================
// Render de subelemento (detalle) "como en la imagen"
// =====================
function statusChipForDetail({ fuente, ym, detail }) {
    const recDate = parseDateLoose(detail?.fecha_recepcion);
  const deadline = deadlineDateFromDetail({ fuente, ym, detail });
  const msg = String(detail?.mensaje ?? "").trim();
  const estado = String(detail?.estado ?? "").trim();
  const vacioPermitido =
    detail?.vacio_permitido === true || FUENTES_VACIO_PERMITIDO.has(String(fuente ?? ""));
  const late = (() => {
    if (detail?.fuera_plazo === true) return true;
    return isFueraPlazo({ fuente, ym, detail });
  })();

  // 1) NO llegó (o no hay registro)
  if (!detail?.has || estado === "NO_LLEGO") {
    return {
      icon: <AssignmentLateOutlinedIcon sx={{ fontSize: 18, color: "#e53935" }} />,
      text: "-",
      tip: "Reporte no recepcionado",
    };
  }

  // 2) Error por estado o flag
  if (detail?.error || estado === "LLEGO_CON_ERROR") {
    return {
      icon: <AssignmentLateOutlinedIcon sx={{ fontSize: 18, color: "#e53935" }} />,
      text: recDate ? formatDayMonthES(recDate) : "-",
      tip: msg ? `Reporte con Error: ${msg}` : "Reporte con Error",
    };
  }

// 3) Llegó vacío
if (detail?.vacio || estado === "LLEGO_OK_EN_BLANCO") {

  if (late) {
    return {
      icon: <AssignmentLateOutlinedIcon sx={{ fontSize: 18, color: "#e53935" }} />,
      text: recDate ? formatDayMonthES(recDate) : "-",
      tip: "Reporte vacío fuera de plazo",
    };
  }

  if (vacioPermitido) {
    return {
      icon: <AssignmentLateOutlinedIcon sx={{ fontSize: 18, color: "#9e9e9e" }} />,
      text: recDate ? formatDayMonthES(recDate) : "-",
      tip: "Reporte vacío (permitido)",
    };
  }

  return {
    icon: <AssignmentLateOutlinedIcon sx={{ fontSize: 18, color: "#e53935" }} />,
    text: recDate ? formatDayMonthES(recDate) : "-",
    tip: "Reporte vacío no permitido",
  };
}

  if (recDate) {
    const inTime = recDate.getTime() <= deadline.getTime();
    if (inTime) {
      return {
        icon: <TaskOutlinedIcon sx={{ fontSize: 18, color: "#2e7d32" }} />,
        text: formatDayMonthES(recDate),
        tip: "Reporte OK",
      };
    }
    return {
      icon: <TaskOutlinedIcon sx={{ fontSize: 18, color: "#e53935" }} />,
      text: formatDayMonthES(recDate),
      tip: "Recepcionado con retraso",
    };
  }

  return {
    icon: <TaskOutlinedIcon sx={{ fontSize: 18, color: "#2e7d32" }} />,
    text: "-",
    tip: "Reporte recepcionado",
  };
}

export default function HistorialReportes() {
  const { userSesion, includeRol } = useContext(GeneralContext);
  const isOpRol = includeRol?.([ROL_OP_ADMIN, ROL_OP_INS]);
  const restrictToUserOperador = isOpRol && !!userSesion?.id_operador;

  const [loading, setLoading] = useState(false);
  const [apiData, setApiData] = useState(null);

  const [selectedFuente, setSelectedFuente] = useState(null);
  const [selectedOperador, setSelectedOperador] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);

  const [expandedOps, setExpandedOps] = useState(() => new Set());

  const yearButtons = useMemo(() => {
    const ys = buildYearRange(2025, CURRENT_YEAR);
    return ys.sort((a, b) => b - a);
  }, []);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        const resp = await axios.getHistorialReportes?.(
          restrictToUserOperador ? userSesion.id_operador : undefined
        );
        const data = resp?.data ?? resp;

        if (!alive) return;

        const normalized = normalizeApiData(data);
        setApiData(normalized);

        const yearsFromData = extractYears(normalized);
        const y0 = yearsFromData?.find((y) => y >= 2025 && y <= CURRENT_YEAR) ?? CURRENT_YEAR;

        setSelectedYear(y0);
        setSelectedFuente(null);
        setSelectedOperador(null);
        setExpandedOps(new Set());
      } catch (e) {
        if (!alive) return;
        console.error("Error cargando historial:", e);
        setApiData(null);
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, []); 



  const grid = useMemo(() => {
    if (!apiData || !selectedYear) return { idx: new Map(), fuentes: [], months: [], rows: [] };
    return buildGrid({ apiData, selectedYear, selectedFuente });
  }, [apiData, selectedYear, selectedFuente]);

  const operatorOptions = useMemo(() => grid.rows.map((r) => r.operador), [grid.rows]);

  const visibleRows = useMemo(() => {
    if (!selectedOperador) return grid.rows;
    return grid.rows.filter((r) => r.operador === selectedOperador);
  }, [grid.rows, selectedOperador]);

  const toggleExpand = (opName) => {
    setExpandedOps((prev) => {
      const next = new Set(prev);
      if (next.has(opName)) next.delete(opName);
      else next.add(opName);
      return next;
    });
  };

  const collapseAllOps = () => setExpandedOps(new Set());
  const expandAllOps = () => setExpandedOps(new Set(visibleRows.map((r) => r.operador)));

  const isAllExpanded = useMemo(() => {
    if (!visibleRows.length) return false;
    return visibleRows.every((r) => expandedOps.has(r.operador));
  }, [visibleRows, expandedOps]);

  const handleYearSelect = (y) => {
    setSelectedYear(y);
    setExpandedOps(new Set());
  };

  const handleFilter = (item, value) => {
    if (item === "reporte") {
      setSelectedFuente(value ?? null);
      setExpandedOps(new Set());
      return;
    }
    if (item === "operador") {
      setSelectedOperador(value ?? null);
      setExpandedOps(new Set());
      return;
    }
  };

const detailRowsForOperator = (opName) => {
  const fuentes =
    selectedFuente !== null
      ? [selectedFuente]
      : (grid.fuentes ?? []).slice().sort((a, b) => a.localeCompare(b));

  const months = grid.months ?? [];
  const dIndex = apiData?._detailIndex;

  return fuentes.map((fuente) => {
    const byOp = dIndex?.get?.(fuente);
    const byYm = byOp?.get?.(opName);

    const cells = months.map((ym) => {
      const detail = byYm?.get?.(ym) ?? { has: false };
      return { ym, fuente, detail };
    });

    return { fuente, cells };
  });
};

  const tableFixedSx = {
    tableLayout: "fixed",
    width: OP_COL_W + MONTH_COL_W * 12,
    "& th, & td": { boxSizing: "border-box", mx: 0 },
  };

  const monthCellSx = { width: MONTH_COL_W, minWidth: MONTH_COL_W };
  const subMonthCellSx = { width: MONTH_COL_W, minWidth: MONTH_COL_W, bgcolor: "background.paper" };

  const stickyFirstColBodySx = {
    width: OP_COL_W - DETAIL_PAD_W,
    minWidth: OP_COL_W - DETAIL_PAD_W,
    position: "sticky",
    left: 0,
    zIndex: 3,
    bgcolor: "background.paper",
  };

  const stickyHeadSx = {
    position: "sticky",
    top: 0,
    zIndex: 2,
    bgcolor: "background.paper",
  };

  const stickyFirstColHeadSx = {
    width: OP_COL_W,
    minWidth: OP_COL_W,
    position: "sticky",
    left: 0,
    top: 0,
    zIndex: 10,
    bgcolor: "background.paper",
  };

  const stickyDetailPadSx = {
    width: DETAIL_PAD_W,
    minWidth: DETAIL_PAD_W,
    position: "sticky",
    left: 0,
    zIndex: 6,
    borderBottom: "1px solid #f6f6f6",
    bgcolor: "#f6f6f6",
  };

  const stickyDetailFirstColSx = {
    ...stickyFirstColBodySx,
    left: DETAIL_PAD_W,
    zIndex: 5,
    bgcolor: "background.paper",
  };

  const yearColSize = useMemo(() => {
    const n = yearButtons.length || 1;
    return Math.max(2, Math.floor(12 / n));
  }, [yearButtons.length]);

  const expandedRowSx = {
    backgroundColor: "#B0C9E2!important",
  };
  const reportOptions = useMemo(() => {
  const fuentes = (apiData?.fuentes ?? []).map((x) => x.fuente);
  const uniq = [...new Set(fuentes)].sort((a, b) => a.localeCompare(b));

  return uniq.map((fuente) => ({
    value: fuente,                
    label: labelFromApi(fuente),
  }));
}, [apiData]);

  return (
    <PageBody>
      <Box sx={{ p: 2 }}>
        <Stack spacing={1} alignItems="center" sx={{ mb: 3 }}>
          <Typography
            variant="h1"
            color="inherit"
            noWrap
            sx={{ textAlign: "center", padding: "0.5rem" }}
          >
            ESTADO DE RECEPCIÓN DE REPORTES
          </Typography>

          <Grid container spacing={1} alignItems="center" sx={{ mt: 0.5, width: "100%" }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <InputFilter
                item="reporte"
                name="Reporte"
                data={[]}
                opt={reportOptions} 
                value={selectedFuente}
                callback={handleFilter}
                disabled={!apiData || loading}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <InputFilter
                item="operador"
                name="Operador"
                data={[]}
                opt={operatorOptions}
                defaultOpt={[]}
                value={selectedOperador}
                callback={handleFilter}
                disabled={!apiData || loading}
              />
            </Grid>
          </Grid>

          <Grid container spacing={1} alignItems="center" sx={{ mt: 0.5, width: "100%" }}>
            {yearButtons.map((y) => {
              const active = selectedYear === y;
              const showIcon = active;

              return (
                <Grid key={y}  size={{ xs: 12, sm: yearColSize, md: yearColSize }}>
  <Box sx={{ position: "relative", width: "100%" }}>
    <Button
      fullWidth
      variant="contained"
      disableElevation
      disabled={!apiData || loading}
      onClick={() => handleYearSelect(y)}
      sx={yearBtnSx(active)}
    >
      {y}
    </Button>

    {active ? (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          if (isAllExpanded) collapseAllOps();
          else expandAllOps();
        }}
        sx={{
          position: "absolute",
          right: 6,
          top: "50%",
          transform: "translateY(-50%)",
          bgcolor: "transparent",
        }}
      >
        {isAllExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
      </IconButton>
    ) : null}
  </Box>
</Grid>
              );
            })}
          </Grid>
        </Stack>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : !apiData ? (
          <Paper sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
            Sin datos
          </Paper>
        ) : (
          <>
            <Box
              variant="outlined"
              sx={{
                px: 2,
                py: 1,
                bgcolor: "#AFC7DF",
                borderColor: "rgba(0,0,0,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography sx={{ fontWeight: 800 }}>
                Conteo consolidado de Reportes {selectedYear}
              </Typography>

              <IconButton
                size="small"
                onClick={() => {
                  if (isAllExpanded) collapseAllOps();
                  else expandAllOps();
                }}
                disabled={ loading || !apiData}
              >
                {isAllExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
              </IconButton>
            </Box>

            <TableContainer
              component={Box}
              variant="outlined"
              sx={{
                maxHeight: "70vh",
                overflowY: "scroll",
                scrollbarGutter: "stable",
              }}
            >
              <Table size="small" stickyHeader sx={tableFixedSx}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...stickyFirstColHeadSx, fontWeight: 700 }}>
                      Operador
                    </TableCell>

                    {MONTHS.map((m) => (
                      <TableCell
                        key={m.key}
                        align="center"
                        sx={{ ...monthCellSx, ...stickyHeadSx, fontWeight: 700 }}
                      >
                        {m.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {visibleRows.map((r) => {
                    const allowExpand = true;
                    const isExpanded = expandedOps.has(r.operador);
                    const detailRows = allowExpand ? detailRowsForOperator(r.operador) : [];

                    return (
                      <React.Fragment key={r.operador}>
                        <TableRow sx={isExpanded ? expandedRowSx : undefined}>
                          <TableCell
                            sx={{
                              ...stickyFirstColBodySx,
                              fontWeight: 700,
                              bgcolor: isExpanded ? expandedRowSx : "background.paper",
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1}>
                              {allowExpand ? (
                                <IconButton size="small" onClick={() => toggleExpand(r.operador)}>
                                  {isExpanded ? (
                                    <KeyboardArrowDownIcon />
                                  ) : (
                                    <KeyboardArrowRightIcon />
                                  )}
                                </IconButton>
                              ) : (
                                <Box sx={{ width: 34 }} />
                              )}
                              <span>{r.operador}</span>
                            </Stack>
                          </TableCell>

                          {r.cells.map((c) => (
                            <TableCell
                              key={c.ym}
                              align="center"
                              sx={{
                                ...monthCellSx,
                                bgcolor: isExpanded ? expandedRowSx : "background.paper",
                              }}
                            >
                              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "999px",
                                    bgcolor: dotColor(c.present, c.total),
                                  }}
                                />
                                <Typography component="span" sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                                  {c.present}/{c.total}
                                </Typography>
                              </Box>
                            </TableCell>
                          ))}
                        </TableRow>

                        {allowExpand ? (
                          <TableRow>
                            <TableCell colSpan={13} sx={{ p: 0, border: 0 }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Table size="small" sx={tableFixedSx}>
                                  <TableBody>
                                    {detailRows.map((d) => (
                                      <TableRow key={d.fuente} hover>
                                        <TableCell sx={stickyDetailPadSx} />
                                        <TableCell sx={{ ...stickyDetailFirstColSx, px: 4 }}>
                                          <Typography sx={{ fontSize: "0.86rem", fontWeight: 700 }}>
                                            {labelFromApi(d.fuente)}
                                          </Typography>
                                        </TableCell>

                                        {d.cells.map((c) => {
                                          const view = statusChipForDetail({
                                            fuente: c.fuente,
                                            ym: c.ym,
                                            detail: c.detail,
                                          });

                                          return (
                                            <TableCell key={c.ym} align="center" sx={subMonthCellSx}>
                                              <BlueTooltip
                                                title={
                                                  <Box sx={{ py: 0.5, px: 0.25 }}>
                                                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 700 }}>
                                                      {view.tip}
                                                    </Typography>
                                                  </Box>
                                                }
                                                placement="top"
                                                arrow
                                              >
                                                <Box
                                                  sx={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: 0.75,
                                                    cursor: "default",
                                                  }}
                                                >
                                                  {view.icon}
                                                  <Typography component="span" sx={{ fontSize: "0.82rem", fontWeight: 600 }}>
                                                    {view.text}
                                                  </Typography>
                                                </Box>
                                              </BlueTooltip>
                                            </TableCell>
                                          );
                                        })}
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>
    </PageBody>
  );
}